import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { KstDataGrid, type KstDataGridColumn, type KstDataGridRows, type KstDataGridPager, type KstDataGridHeaderButton } from '@/components/KstDataGrid';
import { AdminTabBar } from '@/components/AdminTabBar';
import { ConfirmModal } from '@/components/ConfirmModal';
import { AddCallSignModal } from '@/components/modals/AddCallSignModal';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import { hasValue } from '@/utils/legacy-utils';
import moment from 'moment';
// Lazy wrappers: these window globals are set by MyBuiltScripts.js which loads
// after ReactBundle.js. Wrapping them defers resolution to call time.
const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const deepCopyProperties = _wg('deepCopyProperties');
const getSubmoduleOrKerktonRoleFromLogEntry = _wg('getSubmoduleOrKerktonRoleFromLogEntry');
const indexOfValueInArrayByProperty = _wg('indexOfValueInArrayByProperty');

const tabs = [
  { id: 'showManageCallSignTab', label: 'Call Signs' },
  { id: 'showCallSignAuditLogTab', label: 'Audit Log' },
];

const callSignColumns: KstDataGridColumn[] = [
  { field: 'id', name: 'Id', size: 'hidden' },
  {
    field: 'callSign',
    name: 'Call Sign',
    size: 'col-xs-10',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.callSign)) {
        return e.callSign;
      }
      return 'Error getting Call Sign.';
    },
  },
];

const callSignHeaderButtons: KstDataGridHeaderButton[] = [
  { name: 'Add', function: 'add', src: '/Content/TourImages/add-r.svg' },
  { name: 'Delete', function: 'delete', src: '/Content/TourImages/trash.svg' },
];

const auditLogColumns: KstDataGridColumn[] = [
  {
    field: 'dateCreated',
    name: 'Date / Time',
    size: 'col-xs-2',
    template: (e: any) => {
      if (hasValue(e) && hasValue(e.dateCreated)) {
        return moment(e.dateCreated).format('MM/DD/YYYY HH:mm:ss');
      }
      return 'Error getting date / time.';
    },
  },
  {
    field: 'loggedByFirstName',
    name: 'First',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByFirstName) ? e.loggedByFirstName : ''),
  },
  {
    field: 'loggedByLastName',
    name: 'Last',
    size: 'col-xs-2',
    template: (e: any) => (hasValue(e) && hasValue(e.loggedByLastName) ? e.loggedByLastName : ''),
  },
  {
    field: 'role',
    name: 'Role',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.role) ? getSubmoduleOrKerktonRoleFromLogEntry(e) : ''),
  },
  {
    field: 'action',
    name: 'Action',
    size: 'col-xs-1',
    template: (e: any) => (hasValue(e) && hasValue(e.action) ? e.action : ''),
  },
  {
    field: 'entry',
    name: 'Entry',
    size: 'col-xs-4',
    template: (e: any) => (hasValue(e) && hasValue(e.entry) ? e.entry : ''),
  },
];

export function CallSignManagementScreen() {
  const { theme } = useTheme();

  const [activeTab, setActiveTab] = useState('showManageCallSignTab');
  const [activePage, setActivePage] = useState('showManageCallSign');

  // Call sign management grid
  const [callSignEntries, setCallSignEntries] = useState<any[] | null>(null);
  const callSignListCacheRef = useRef<any>(null);

  // Loading states
  const [isCallSignsLoading, setIsCallSignsLoading] = useState<boolean>(false);
  const [isAuditLogLoading, setIsAuditLogLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // Audit log grid
  const [auditLogEntries, setAuditLogEntries] = useState<any[]>([]);
  const [auditLogPager, setAuditLogPager] = useState<KstDataGridPager>({
    curPage: null as any,
    numPages: null as any,
    records: null as any,
    isVisible: false,
  });

  // Add Call Sign modal
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Delete confirm modal
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmItems, setDeleteConfirmItems] = useState<any[]>([]);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');

  // Grid ref for scroll
  const gridContainerRef = useRef<HTMLDivElement>(null);

  const callSignRows: KstDataGridRows = useMemo(
    () => ({ entries: callSignEntries }),
    [callSignEntries]
  );

  const auditLogRows: KstDataGridRows = useMemo(
    () => ({ entries: auditLogEntries }),
    [auditLogEntries]
  );

  const clearCachedValues = useCallback(() => {
    callSignListCacheRef.current = null;
  }, []);

  const getCallSignListPromise = useCallback((): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (hasValue(callSignListCacheRef.current)) {
        resolve(deepCopyProperties(callSignListCacheRef.current));
      } else {
        api.listCompanyCallSigns(true, (result: any) => {
          callSignListCacheRef.current = result;
          resolve(deepCopyProperties(result));
        }, (errorResult: any) => {
          reject(errorResult);
          App.showPopupNotification('Error getting Call Signs.', 'error');
        });
      }
    });
  }, []);

  const refreshCallSignList = useCallback((doScrollToBottom = false) => {
    setIsCallSignsLoading(true);
    getCallSignListPromise().then((listResponse: any) => {
      const callSigns = listResponse.callSigns || [];
      callSigns.sort((a: any, b: any) => a.listIdx - b.listIdx);
      for (let i = 0; i < callSigns.length; i++) {
        callSigns[i].listIdx = i + 1;
        callSigns[i].isListEnabled = true;
      }
      setCallSignEntries(callSigns);
      setIsCallSignsLoading(false);

      if (doScrollToBottom) {
        setTimeout(() => {
          const scrollEl = gridContainerRef.current?.closest('.kst-main-content');
          if (scrollEl) {
            scrollEl.scrollTop = scrollEl.scrollHeight;
          }
        }, 0);
      }
    }).catch(() => {
      setIsCallSignsLoading(false);
    });
  }, [getCallSignListPromise]);

  // Load on mount
  useEffect(() => {
    refreshCallSignList();
  }, [refreshCallSignList]);

  const getAuditLogPage = useCallback((pageNum: number) => {
    setIsAuditLogLoading(true);
    api.getCallSignAuditLogEntries({
      data: {
        page: pageNum,
        pageSize: 50,
        filter: {
          logic: 'and',
          filters: [
            { field: 'action', operator: 'eq', value: 'Assigned' },
          ],
        },
      },
    }, (result: any) => {
      setAuditLogEntries(result.logs);
      setAuditLogPager({
        curPage: pageNum,
        numPages: result.numPages,
        records: result.records,
        isVisible: true,
      });
      setIsAuditLogLoading(false);
    }, () => {
      App.showPopupNotification('Error getting audit log.', 'error');
      setIsAuditLogLoading(false);
    });
  }, []);

  const handleTabChange = useCallback((tabId: string) => {
    setActiveTab(tabId);
    if (tabId === 'showManageCallSignTab') {
      setActivePage('showManageCallSign');
    } else if (tabId === 'showCallSignAuditLogTab') {
      setActivePage('showCallSignAuditLog');
      getAuditLogPage(1);
    }
  }, [getAuditLogPage]);

  const handleButtonCommand = useCallback((functionName: string, checkedItems: any[]) => {
    if (functionName === 'add') {
      setAddModalOpen(true);
    } else if (functionName === 'delete') {
      if (checkedItems.length === 0) {
        App.showPopupNotification('Call Sign must be selected to delete.', 'warning');
        return;
      }
      const count = checkedItems.length;
      const message = count === 1
        ? 'Are you sure you want to delete this call sign?'
        : `Are you sure you want to delete ${count} call signs?`;
      setDeleteConfirmItems(checkedItems);
      setDeleteConfirmMessage(message);
      setDeleteConfirmOpen(true);
    }
  }, []);

  const handleAddSave = useCallback((data: { callSign: string }) => {
    const callSignData = { callSign: data.callSign };
    setIsSaving(true);
    api.saveCompanyCallSign(callSignData, () => {
      setIsSaving(false);
      clearCachedValues();
      refreshCallSignList(true);
      setAddModalOpen(false);
    }, () => {
      setIsSaving(false);
      App.showPopupNotification('Failed to save Call Sign.', 'error');
    });
  }, [clearCachedValues, refreshCallSignList]);

  const handleConfirmDelete = useCallback(() => {
    const idList = deleteConfirmItems.map((elt: any) => elt.id);
    const callSignData = { idList };
    setIsDeleting(true);
    api.deleteCompanyCallSigns(callSignData, () => {
      setIsDeleting(false);
      clearCachedValues();
      refreshCallSignList();
      setDeleteConfirmOpen(false);
      setDeleteConfirmItems([]);
      setDeleteConfirmMessage('');
    }, () => {
      setIsDeleting(false);
      App.showPopupNotification('Failed to delete Call Sign.', 'error');
    });
  }, [deleteConfirmItems, clearCachedValues, refreshCallSignList]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmItems([]);
    setDeleteConfirmMessage('');
  }, []);

  const handleRowMoved = useCallback((row: any) => {
    setCallSignEntries((prev) => {
      const values = [...(prev ?? [])];
      const idxOfRow = values.findIndex((v: any) => v.id === row.id);
      if (idxOfRow >= 0) {
        values.splice(idxOfRow, 1);
      }
      values.splice(row.listIdx - 1, 0, row);
      for (let i = 0; i < values.length; i++) {
        values[i].listIdx = i + 1;
      }

      // Auto-save after reordering
      const idAndListIdxList = values.map((v: any) => ({ id: v.id, listIdx: v.listIdx }));
      idAndListIdxList.sort((a: any, b: any) => a.listIdx - b.listIdx);
      const idList = idAndListIdxList.map((elt: any) => elt.id);
      const callSignData = { idList };
      api.reorderCompanyCallSigns(callSignData, () => {
        clearCachedValues();
      }, () => {
        App.showPopupNotification('Failed to reorder call signs.', 'error');
      });

      return values;
    });
  }, [clearCachedValues]);

  const handleAuditLogPageChange = useCallback((page: number) => {
    getAuditLogPage(page);
  }, [getAuditLogPage]);

  return (
    <div className={theme}>
      {/* Modals */}
      <AddCallSignModal
        open={addModalOpen}
        theme={theme}
        saving={isSaving}
        onSave={handleAddSave}
        onCancel={() => setAddModalOpen(false)}
      />

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete Call Sign"
        message={deleteConfirmMessage}
        confirmText="Delete"
        confirmDisabled={isDeleting}
        busy={isDeleting}
        theme={theme}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      <div className="kst-page-container">
        {/* Mobile Tab Navigation */}
        <div className="kst-mobile-nav">
          <AdminTabBar tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
        </div>

        <div className="kst-layout">
          {/* Desktop Sidebar Navigation */}
          <div className="kst-sidebar">
            <ul className="kst-sidebar-list">
              <li
                className={`kst-sidebar-item${activeTab === 'showManageCallSignTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showManageCallSignTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 8V12L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.5 5.5L7 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M17 7L18.5 5.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
                <span className="kst-sidebar-text">Call Signs</span>
              </li>
              <li
                className={`kst-sidebar-item${activeTab === 'showCallSignAuditLogTab' ? ' kst-sidebar-item--active' : ''}`}
                onClick={() => handleTabChange('showCallSignAuditLogTab')}
              >
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <polyline points="14,2 14,8 20,8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <line x1="16" y1="13" x2="8" y2="13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <line x1="16" y1="17" x2="8" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  <polyline points="10,9 9,9 8,9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="kst-sidebar-text">Audit Log</span>
              </li>
            </ul>
          </div>

          {/* Main Content Area */}
          <div className="kst-main-content">
            {/* Manage Call Signs Page */}
            {activePage === 'showManageCallSign' && (
              <div className="kst-page" ref={gridContainerRef}>
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Call Sign Management</h3>
                  <p className="kst-page-description">
                    Manage the list of available call signs. When call signs are defined here, users must select from this list in their profile and when entering the Mobile module.
                  </p>
                </div>

                <KstDataGrid
                  columns={callSignColumns}
                  rows={callSignRows}
                  headerbuttons={callSignHeaderButtons}
                  showcheckbox={true}
                  showordercolumn={true}
                  mobiletitlefield="callSign"
                  loading={isCallSignsLoading}
                  onButtonCommand={handleButtonCommand}
                  onRowMoved={handleRowMoved}
                />
              </div>
            )}

            {/* Audit Log Page */}
            {activePage === 'showCallSignAuditLog' && (
              <div className="kst-page">
                <div className="kst-page-header">
                  <h3 className="kst-page-title">Call Sign Audit Log</h3>
                  <p className="kst-page-description">
                    View a history of call sign assignments. Each entry shows when a user's call sign has been updated.
                  </p>
                </div>

                <KstDataGrid
                  columns={auditLogColumns}
                  rows={auditLogRows}
                  pager={auditLogPager}
                  showcheckbox={false}
                  showheader={false}
                  mobiletitlefield="entry"
                  loading={isAuditLogLoading}
                  onGoToPage={handleAuditLogPageChange}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CallSignManagementScreen;

